import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

type Side = 'top' | 'bottom' | 'right';

interface TooltipProps {
  /** What the tooltip says. Nothing → the children render alone. */
  content: ReactNode;
  children: ReactNode;
  side?: Side;
  /** Extra classes on the wrapper (an inline-flex span). */
  className?: string;
  /** Delay before showing, in ms. */
  delay?: number;
  /** Also open on keyboard focus (default). Off for a field one types in, where it would cover the next ones. */
  openOnFocus?: boolean;
}

const SHOW_DELAY = 150;
const HIDE_DELAY = 120;
const GAP = 8;
const VIEWPORT_MARGIN = 8;

/**
 * Tooltip shown on hover and on keyboard focus, rendered in a portal with fixed
 * positioning so it is never clipped by a scrolling table or a modal. The content
 * may hold a link: the tooltip stays open while the pointer is over it.
 */
export function Tooltip({ content, children, side = 'top', className = '', delay = SHOW_DELAY, openOnFocus = true }: TooltipProps) {
  const id = useId();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; side: Side } | null>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  // Horizontal shift applied once rendered, so the bubble never leaves the viewport (the arrow stays on the anchor).
  const [shift, setShift] = useState(0);

  const clearTimers = () => {
    if (showTimer.current) clearTimeout(showTimer.current);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    showTimer.current = null;
    hideTimer.current = null;
  };

  const place = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    if (side === 'right') {
      setPosition({ top: rect.top + rect.height / 2, left: rect.right + GAP, side });
      return;
    }
    const actualSide: Side = side === 'top' && rect.top < 80 ? 'bottom' : side;
    setPosition({
      top: actualSide === 'top' ? rect.top - GAP : rect.bottom + GAP,
      left: rect.left + rect.width / 2,
      side: actualSide,
    });
  }, [side]);

  const show = () => {
    clearTimers();
    showTimer.current = setTimeout(() => {
      place();
      setOpen(true);
    }, delay);
  };

  const hide = () => {
    clearTimers();
    hideTimer.current = setTimeout(() => {
      setOpen(false);
      setShift(0);
    }, HIDE_DELAY);
  };

  const close = useCallback(() => {
    clearTimers();
    setOpen(false);
    setShift(0);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    // A click or a scroll anywhere else means the pointer has moved on, even when no
    // mouseleave reached us (touch, automation, the anchor re-rendered away).
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (anchorRef.current?.contains(target) || tipRef.current?.contains(target)) return;
      close();
    };
    const onScroll = () => close();
    const onResize = () => place();
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, place, close]);

  useEffect(() => clearTimers, []);

  useLayoutEffect(() => {
    // Beside its anchor, the bubble is not centred on it: nothing to shift
    if (!open || !position || position.side === 'right') return;
    const el = tipRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const unshiftedLeft = r.left - shift;
    const unshiftedRight = r.right - shift;
    let next = 0;
    if (unshiftedRight > window.innerWidth - VIEWPORT_MARGIN) next = window.innerWidth - VIEWPORT_MARGIN - unshiftedRight;
    if (unshiftedLeft + next < VIEWPORT_MARGIN) next = VIEWPORT_MARGIN - unshiftedLeft;
    // Whole pixels, and a change of less than one ignored: the measured box moves by fractions
    // of a pixel with the shift itself, and comparing exact values re-rendered forever near the
    // left edge of the screen (a blank page, hovering the collapsed menu)
    next = Math.round(next);
    if (Math.abs(next - shift) >= 1) setShift(next);
  }, [open, position, shift]);

  if (content === null || content === undefined || content === false) return <>{children}</>;

  return (
    <span
      ref={anchorRef}
      className={`inline-flex ${className}`}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={openOnFocus ? show : undefined}
      onBlur={openOnFocus ? hide : undefined}
      aria-describedby={open ? id : undefined}
    >
      {children}
      {open &&
        position &&
        createPortal(
          <div
            ref={tipRef}
            id={id}
            role="tooltip"
            onMouseEnter={clearTimers}
            onMouseLeave={hide}
            className="fixed z-[70] rounded-lg bg-neutral-900 px-3 py-2 text-xs leading-relaxed text-white shadow-lg animate-fade-in"
            style={{
              top: position.top,
              left: position.left + shift,
              width: 'max-content',
              maxWidth: `min(20rem, calc(100vw - ${2 * VIEWPORT_MARGIN}px))`,
              transform:
                position.side === 'right'
                  ? 'translate(0, -50%)'
                  : position.side === 'top'
                    ? 'translate(-50%, -100%)'
                    : 'translate(-50%, 0)',
            }}
          >
            {content}
            <span
              className={`absolute h-2 w-2 rotate-45 bg-neutral-900 ${position.side === 'right' ? '-translate-y-1/2' : '-translate-x-1/2'}`}
              style={
                position.side === 'right'
                  ? { left: -4, top: '50%' }
                  : { left: `calc(50% - ${shift}px)`, ...(position.side === 'top' ? { bottom: -4 } : { top: -4 }) }
              }
              aria-hidden="true"
            />
          </div>,
          document.body,
        )}
    </span>
  );
}
